import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useHeaderExtra } from '@/contexts/header.context'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Building2,
  Check,
  Globe,
  Layers,
  TriangleAlert,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Heading, Text } from '@/components/ui/typography'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import { WeeklyHoursSchedule } from '@/components/common/WeeklyHoursSchedule'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useAuth } from '@/hooks/use-auth'
import { DirectoryProvider, useDirectory } from '@/contexts/directory.context'
import {
  HandoffWorkflowsProvider,
  useHandoffWorkflows,
} from '@/contexts/handoff-workflows.context'
import { workflowsApiClient } from '@/lib/api/workflows.client'
import { propertiesApiClient } from '@/lib/api/properties.client'
import {
  directoryApiClient,
  type ActionContact,
  type PropertyGroupWithMembers,
} from '@/lib/api/directory.client'
import { directoryAdminApiClient } from '@/lib/api/directory-admin.client'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import {
  getPropertyDisplayLabel,
  getPropertyDisplaySublabel,
  normalizeActionGroupsForSave,
} from '@/lib/utils/handoff-form-utils'
import {
  parseMentions,
  syncContactsForActionGroups,
} from '@/lib/handoff-mentions'
import {
  buildPushOptions,
  contactChannelOf,
  deriveInstructionsSummary,
  editorToWorkflowGroups,
  workflowToEditorGroups,
} from '@/lib/handoff-rule.converters'
import {
  ActionEditor,
  DEFAULT_TRANSFER_SETTINGS,
  type ActionGroup as EditorActionGroup,
  type ActionRowType,
  type PushFieldDropdownItem,
  type PushOption,
} from '@/components/actions/ActionEditor'
import {
  useDropdownSourceFetching,
  type DropdownSourceFetchers,
} from '@/pages/actions/useDropdownSourceFetching'
import {
  mapStaffUserToItem,
  mapVendorToItem,
  mapTaskCategoryToItem,
} from '@/lib/api/integrations/buildium.utils'
import { mapAssigneeToItem } from '@/lib/api/integrations/hubspot.utils'
import {
  DEFAULT_WEEKLY_SCHEDULE,
  OfficeHoursMode,
  officeHoursToApiValue,
  parseOfficeHours,
  type Integration,
  type Property,
  type WeeklySchedule,
  type WorkflowWithActionGroups,
} from '@super/shared-types'

const GROUP_TYPE_OPTIONS = ['OWNER', 'TENANT', 'MANAGER', 'VENDOR'] as const
type GroupType = (typeof GROUP_TYPE_OPTIONS)[number]

const OFFICE_HOURS_OPTIONS: { value: OfficeHoursMode; label: string }[] = [
  { value: OfficeHoursMode.ALWAYS, label: 'Always' },
  { value: OfficeHoursMode.BUSINESS_HOURS, label: 'Business Hours Only' },
  { value: OfficeHoursMode.AFTER_HOURS, label: 'After Hours Only' },
  { value: OfficeHoursMode.CUSTOM_HOURS, label: 'Custom Times' },
]

const createDefaultCustomSchedule = (): WeeklySchedule =>
  JSON.parse(JSON.stringify(DEFAULT_WEEKLY_SCHEDULE))

const ALL_PROPERTIES_ID = 'all-properties'
const ALL_GROUPS_ID = 'all-groups'

const allPropertiesItem: SearchDropdownItem<Property | null> = {
  id: ALL_PROPERTIES_ID,
  label: 'All properties',
  sublabel: 'Apply this rule to all properties',
  icon: <Globe className="h-4 w-4" />,
  data: null,
  isExclusive: true,
}

const allGroupsItem: SearchDropdownItem<GroupType | null> = {
  id: ALL_GROUPS_ID,
  label: 'All groups',
  sublabel: 'Apply this rule to all caller groups',
  icon: <Globe className="h-4 w-4" />,
  data: null,
  isExclusive: true,
}

const groupTypeDropdownItems: SearchDropdownItem<GroupType | null>[] = [
  allGroupsItem,
  {
    id: 'OWNER',
    label: 'Owner',
    sublabel: 'Property owners',
    icon: <Users className="h-4 w-4" />,
    data: 'OWNER',
  },
  {
    id: 'TENANT',
    label: 'Tenant',
    sublabel: 'Current tenants',
    icon: <Users className="h-4 w-4" />,
    data: 'TENANT',
  },
  {
    id: 'MANAGER',
    label: 'Manager',
    sublabel: 'Property managers',
    icon: <Users className="h-4 w-4" />,
    data: 'MANAGER',
  },
  {
    id: 'VENDOR',
    label: 'Vendor',
    sublabel: 'Vendors and contractors',
    icon: <Users className="h-4 w-4" />,
    data: 'VENDOR',
  },
]

const createPropertyDropdownItem = (
  property: Property,
): SearchDropdownItem<Property> => ({
  id: property.id,
  label: getPropertyDisplayLabel(property),
  sublabel: getPropertyDisplaySublabel(property),
  icon: <Building2 className="h-4 w-4" />,
  data: property,
})

// Group items share the property dropdown (matching is properties OR groups).
// data stays null — selections are partitioned by id against the group list.
const createPropertyGroupDropdownItem = (
  group: PropertyGroupWithMembers,
): SearchDropdownItem<Property | null> => ({
  id: group.id,
  label: group.name,
  sublabel: `Group · ${group.propertyIds.length} ${group.propertyIds.length === 1 ? 'property' : 'properties'}`,
  icon: <Layers className="h-4 w-4" />,
  data: null,
})

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Math.random().toString(36).slice(2, 10)}`

/**
 * Dedicated page for creating / editing a single handoff rule. Replaces
 * the legacy modal flow:
 *   /actions/handoff/new       — create
 *   /actions/handoff/:id       — edit
 */
export function HandoffRulePage() {
  const { currentCompany } = useAppAuth()
  const { isSuperAdmin } = useAuth()
  const [properties, setProperties] = useState<Property[]>([])
  const [propertyGroups, setPropertyGroups] = useState<
    PropertyGroupWithMembers[]
  >([])
  const [propertiesLoading, setPropertiesLoading] = useState(true)

  useEffect(() => {
    if (!currentCompany?.id) {
      setPropertiesLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setPropertiesLoading(true)
        const [fetched, fetchedGroups] = await Promise.all([
          isSuperAdmin
            ? propertiesApiClient.getAllAdminPropertiesForCompany(
                currentCompany.id,
              )
            : propertiesApiClient.getProperties(),
          // Groups failing must not block properties — rules degrade to
          // individual-property selection.
          (isSuperAdmin
            ? directoryAdminApiClient.listPropertyGroups(currentCompany.id)
            : directoryApiClient.listPropertyGroups()
          ).catch((): PropertyGroupWithMembers[] => []),
        ])
        if (!cancelled) {
          setProperties(fetched)
          setPropertyGroups(fetchedGroups)
        }
      } catch (e) {
        console.error('Failed to load properties', e)
      } finally {
        if (!cancelled) setPropertiesLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [currentCompany?.id, isSuperAdmin])

  return (
    <DirectoryProvider>
      <HandoffWorkflowsProvider properties={properties}>
        <HandoffRulePageContent
          properties={properties}
          propertyGroups={propertyGroups}
          propertiesLoading={propertiesLoading}
        />
      </HandoffWorkflowsProvider>
    </DirectoryProvider>
  )
}

function HandoffRulePageContent({
  properties,
  propertyGroups,
  propertiesLoading,
}: {
  properties: Property[]
  propertyGroups: PropertyGroupWithMembers[]
  propertiesLoading: boolean
}) {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const {
    findRuleById,
    refreshWorkflows,
    loading: rulesLoading,
  } = useHandoffWorkflows()
  const { roles, staff, loading: directoryLoading } = useDirectory()

  const isEditing = !!id
  const initialRule = id ? findRuleById(id) : undefined

  // ─── Form state ────────────────────────────────────────────────────
  const [title, setTitle] = useState('')
  const [selectionCriteria, setSelectionCriteria] = useState('')
  const [propertyIds, setPropertyIds] = useState<string[]>([])
  const [propertyGroupIds, setPropertyGroupIds] = useState<string[]>([])
  const [groupTypes, setGroupTypes] = useState<GroupType[]>([])
  const [selectedProperties, setSelectedProperties] = useState<
    SearchDropdownItem<Property | null>[]
  >([allPropertiesItem])
  const [selectedGroupTypes, setSelectedGroupTypes] = useState<
    SearchDropdownItem<GroupType | null>[]
  >([allGroupsItem])
  const [officeHours, setOfficeHours] = useState<OfficeHoursMode>(
    OfficeHoursMode.ALWAYS,
  )
  const [customSchedule, setCustomSchedule] = useState<WeeklySchedule>(
    createDefaultCustomSchedule,
  )
  const [actionGroups, setActionGroups] = useState<EditorActionGroup[]>([
    {
      id: newId(),
      name: '',
      when: '',
      actions: [],
      transfer: { ...DEFAULT_TRANSFER_SETTINGS },
    },
  ])
  const [saving, setSaving] = useState(false)

  // Push integration options come from the company's installed integrations.
  // Keep the raw integrations alongside the picker options so the metadata
  // defaults can be re-applied when hydrating an existing rule.
  const [availableIntegrations, setAvailableIntegrations] = useState<
    Integration[]
  >([])
  const [integrationsLoading, setIntegrationsLoading] = useState(true)
  const pushOptions = useMemo<PushOption[]>(
    () => buildPushOptions(availableIntegrations),
    [availableIntegrations],
  )

  // Roles mentioned by the rule that currently have no members. Surfaced as a
  // non-blocking banner so an emptied-out role doesn't silently route to nobody.
  const emptyRoleMentionCount = useMemo(() => {
    const emptyRoleIds = new Set<string>()
    for (const group of actionGroups) {
      for (const action of group.actions) {
        if (action.type === 'PUSH_INTEGRATION') continue
        for (const roleId of parseMentions(action.target, roles, staff)
          .roleIds) {
          const role = roles.find(({ id }) => id === roleId)
          if (role && role.members.length === 0) emptyRoleIds.add(roleId)
        }
      }
    }
    return emptyRoleIds.size
  }, [actionGroups, roles, staff])

  // Async-loaded dropdown items used by push integration handoff-level
  // fields (e.g. Buildium Task assignee, vendor, category). Lazy-fetched
  // when a push field actually needs them.
  const dropdownSourceFetchers: DropdownSourceFetchers = useMemo(() => {
    const stripData = (
      items: ({ id: string; label: string; sublabel?: string } | null)[],
    ): PushFieldDropdownItem[] =>
      items
        .filter(
          (item): item is { id: string; label: string; sublabel?: string } =>
            item !== null,
        )
        .map(item => ({
          id: item.id,
          label: item.label,
          sublabel: item.sublabel,
        }))

    // The pipelines and stages dropdowns are two views of the same endpoint;
    // share one fetch (retryable on failure).
    let pipelinesPromise: ReturnType<
      typeof integrationsClient.hubspot.getTicketPipelines
    > | null = null
    const fetchTicketPipelines = () => {
      pipelinesPromise ??= (
        isSuperAdmin && currentCompany
          ? integrationsClient.hubspot.getAdminTicketPipelines(
              currentCompany.id,
            )
          : integrationsClient.hubspot.getTicketPipelines()
      ).catch(error => {
        pipelinesPromise = null
        throw error
      })
      return pipelinesPromise
    }

    return {
      'buildium-task-categories': async () => {
        const categories =
          isSuperAdmin && currentCompany
            ? await integrationsClient.buildium.getAdminTaskCategories(
                currentCompany.id,
              )
            : await integrationsClient.buildium.getTaskCategories()
        return stripData(categories.map(mapTaskCategoryToItem))
      },
      'buildium-staff-users': async () => {
        const users =
          isSuperAdmin && currentCompany
            ? await integrationsClient.buildium.getAdminStaffUsers(
                currentCompany.id,
              )
            : await integrationsClient.buildium.getStaffUsers()
        return stripData(users.map(mapStaffUserToItem))
      },
      'buildium-vendors': async () => {
        const vendors =
          isSuperAdmin && currentCompany
            ? await integrationsClient.buildium.getAdminVendors(
                currentCompany.id,
              )
            : await integrationsClient.buildium.getVendors()
        return stripData(vendors.map(mapVendorToItem))
      },
      'hubspot-owners': async () => {
        const owners =
          isSuperAdmin && currentCompany
            ? await integrationsClient.hubspot.getAdminAssignees(
                currentCompany.id,
              )
            : await integrationsClient.hubspot.getAssignees()
        return stripData(owners.map(mapAssigneeToItem))
      },
      'hubspot-ticket-pipelines': async () => {
        const pipelines = await fetchTicketPipelines()
        return pipelines.map(p => ({ id: p.id, label: p.label }))
      },
      'hubspot-ticket-stages': async () => {
        const pipelines = await fetchTicketPipelines()
        // Flatten all stages, tagging each with its pipeline id (groupId) so the
        // editor can cascade-filter to the selected pipeline's stages.
        return pipelines.flatMap(p =>
          p.stages.map(s => ({
            id: s.id,
            label: s.label,
            sublabel: p.label,
            groupId: p.id,
          })),
        )
      },
      'doorloop-users': async () => {
        const users =
          isSuperAdmin && currentCompany
            ? await integrationsClient.doorLoop.getAdminUsers(currentCompany.id)
            : await integrationsClient.doorLoop.getUsers()
        return stripData(users.map(mapAssigneeToItem))
      },
      'doorloop-vendors': async () => {
        const vendors =
          isSuperAdmin && currentCompany
            ? await integrationsClient.doorLoop.getAdminVendors(
                currentCompany.id,
              )
            : await integrationsClient.doorLoop.getVendors()
        return stripData(vendors.map(mapAssigneeToItem))
      },
    }
  }, [isSuperAdmin, currentCompany])

  const { dropdownItems, failedSources } = useDropdownSourceFetching(
    actionGroups,
    pushOptions,
    dropdownSourceFetchers,
    integrationsLoading,
  )

  useHeaderExtra(
    <div className="flex items-center">
      <Link
        to="/actions"
        className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md hover:bg-accent transition-colors text-sm text-muted-foreground"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to actions
      </Link>
    </div>,
    [],
  )

  useEffect(() => {
    const companyId = currentCompany?.id
    if (!companyId) {
      setIntegrationsLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setIntegrationsLoading(true)
        const integrations = isSuperAdmin
          ? await integrationsClient.getPushEnabledIntegrationsAdmin(companyId)
          : await integrationsClient.getPushEnabledIntegrations()
        if (cancelled) return
        setAvailableIntegrations(integrations)
      } catch (e) {
        console.error('Failed to load integrations', e)
      } finally {
        if (!cancelled) setIntegrationsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [currentCompany?.id, isSuperAdmin])

  // Hydrate the form once per rule. The contacts fetch is cached by ruleKey
  // so retries triggered by late-arriving deps reuse the same promise
  // instead of refiring the network round-trip.
  const hydratedRuleId = useRef<string | null>(null)
  const contactsPromise = useRef<{
    ruleKey: string
    promise: Promise<Map<string, ActionContact[]>>
  } | null>(null)
  const actionsRef = useRef<HTMLDivElement>(null)
  const [actionsError, setActionsError] = useState(false)
  const [criteriaErrorGroupIds, setCriteriaErrorGroupIds] = useState<
    Set<string>
  >(new Set())
  const [emptyTargetRowIds, setEmptyTargetRowIds] = useState<Set<string>>(
    new Set(),
  )
  const [titleError, setTitleError] = useState(false)
  const [scenarioError, setScenarioError] = useState(false)
  const [actionsHydrated, setActionsHydrated] = useState(false)
  useEffect(() => {
    if (!initialRule) return
    // Wait for every async dep the hydrator reads — without this gate, a
    // contacts fetch that wins the race finalizes the form with stale empty
    // properties/roles/staff/pushOptions, and the dedup ref locks it in.
    if (propertiesLoading || directoryLoading || integrationsLoading) {
      console.debug('[HandoffRulePage] hydrate gated, waiting on deps', {
        ruleId: initialRule.id,
        propertiesLoading,
        directoryLoading,
        integrationsLoading,
      })
      return
    }
    const ruleKey = initialRule.id ?? 'new'
    if (hydratedRuleId.current === ruleKey) return
    setActionsHydrated(false)
    let cancelled = false

    if (contactsPromise.current?.ruleKey !== ruleKey) {
      const groupIds = (initialRule.actionGroups ?? [])
        .map(group => group.id)
        .filter((groupId): groupId is string => !!groupId)
      contactsPromise.current = {
        ruleKey,
        promise: (async () => {
          const map = new Map<string, ActionContact[]>()
          if (groupIds.length === 0) return map
          try {
            const contactLists = await Promise.all(
              groupIds.map(groupId =>
                directoryApiClient.listActionContacts(groupId),
              ),
            )
            for (const list of contactLists) {
              for (const contact of list) {
                const existing = map.get(contact.actionGroupId) ?? []
                existing.push(contact)
                map.set(contact.actionGroupId, existing)
              }
            }
          } catch (error) {
            console.error('Failed to load directory mentions for rule', error)
          }
          return map
        })(),
      }
    }

    contactsPromise.current.promise.then(contactsByGroupId => {
      if (cancelled) return
      setTitle(initialRule.title)
      setSelectionCriteria(initialRule.selectionCriteria)
      setPropertyIds(initialRule.propertyIds ?? [])
      setPropertyGroupIds(initialRule.propertyGroupIds ?? [])
      setGroupTypes((initialRule.groupTypes ?? []) as GroupType[])
      setOfficeHours(parseOfficeHours(initialRule.officeHours))
      setCustomSchedule(
        initialRule.customOfficeHoursSchedule ?? createDefaultCustomSchedule(),
      )
      setActionGroups(
        workflowToEditorGroups(initialRule.actionGroups ?? [], pushOptions, {
          contactsByGroupId,
          roles,
          staff,
        }).map(group => {
          if (group.transfer) return group
          const converted = group.actions.map(a =>
            a.type === 'PHONE_CALL' ? { ...a, type: 'TRANSFER' as const } : a,
          )
          return {
            ...group,
            actions: converted,
            transfer: { ...DEFAULT_TRANSFER_SETTINGS },
          }
        }),
      )

      const selectedPropertyItems = (initialRule.propertyIds ?? [])
        .map(propertyId =>
          properties.find(property => property.id === propertyId),
        )
        .filter(Boolean)
        .map(property => createPropertyDropdownItem(property as Property))
      const selectedGroupItems = (initialRule.propertyGroupIds ?? [])
        .map(groupId => propertyGroups.find(group => group.id === groupId))
        .filter(Boolean)
        .map(group =>
          createPropertyGroupDropdownItem(group as PropertyGroupWithMembers),
        )
      const selected = [...selectedGroupItems, ...selectedPropertyItems]
      setSelectedProperties(
        selected.length > 0 ? selected : [allPropertiesItem],
      )

      if (initialRule.groupTypes && initialRule.groupTypes.length > 0) {
        const selected = initialRule.groupTypes
          .map(groupType =>
            groupTypeDropdownItems.find(item => item.id === groupType),
          )
          .filter(Boolean) as SearchDropdownItem<GroupType | null>[]
        setSelectedGroupTypes(selected)
      } else {
        setSelectedGroupTypes([allGroupsItem])
      }

      hydratedRuleId.current = ruleKey
      setActionsHydrated(true)
    })

    return () => {
      cancelled = true
    }
  }, [
    initialRule,
    properties,
    propertyGroups,
    pushOptions,
    roles,
    staff,
    propertiesLoading,
    directoryLoading,
    integrationsLoading,
  ])

  const propertyDropdownItems: SearchDropdownItem<Property | null>[] = [
    allPropertiesItem,
    ...propertyGroups.map(createPropertyGroupDropdownItem),
    ...properties.map(createPropertyDropdownItem),
  ]
  const propertyGroupIdSet = useMemo(
    () => new Set(propertyGroups.map(group => group.id)),
    [propertyGroups],
  )

  const handlePropertySelect = (
    items:
      | SearchDropdownItem<Property | null>[]
      | SearchDropdownItem<Property | null>
      | null,
  ) => {
    const propertyItems = Array.isArray(items) ? items : items ? [items] : []
    const hasAllProperties = propertyItems.some(
      it => it.id === ALL_PROPERTIES_ID,
    )
    if (hasAllProperties) {
      const found = propertyItems.find(it => it.id === ALL_PROPERTIES_ID)
      if (found) {
        setSelectedProperties([found])
        setPropertyIds([])
        setPropertyGroupIds([])
      }
    } else {
      setSelectedProperties(propertyItems)
      const picked = propertyItems.filter(it => it.id !== ALL_PROPERTIES_ID)
      setPropertyIds(
        picked.filter(it => !propertyGroupIdSet.has(it.id)).map(it => it.id),
      )
      setPropertyGroupIds(
        picked.filter(it => propertyGroupIdSet.has(it.id)).map(it => it.id),
      )
    }
  }

  const handleGroupTypeSelect = (
    items:
      | SearchDropdownItem<GroupType | null>[]
      | SearchDropdownItem<GroupType | null>
      | null,
  ) => {
    const groupItems = Array.isArray(items) ? items : items ? [items] : []
    const hasAllGroups = groupItems.some(it => it.id === ALL_GROUPS_ID)
    if (hasAllGroups) {
      setSelectedGroupTypes([allGroupsItem])
      setGroupTypes([])
    } else {
      setSelectedGroupTypes(groupItems)
      setGroupTypes(
        groupItems
          .filter(it => it.id !== ALL_GROUPS_ID && it.data !== null)
          .map(it => it.id as GroupType),
      )
    }
  }

  // ─── Save ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!currentCompany?.id) {
      toast.error('No company selected')
      return
    }
    const missingTitle = !title.trim()
    const missingScenario = !selectionCriteria.trim()
    if (missingTitle || missingScenario) {
      setTitleError(missingTitle)
      setScenarioError(missingScenario)
      return
    }
    setTitleError(false)
    setScenarioError(false)

    const normalizedActionGroups = normalizeActionGroupsForSave(actionGroups)
    if (normalizedActionGroups.length > 1) {
      const missing = new Set(
        normalizedActionGroups
          .filter(group => !group.when.trim())
          .map(group => group.id),
      )
      if (missing.size > 0) {
        setCriteriaErrorGroupIds(missing)
        actionsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
        return
      }
    }
    setCriteriaErrorGroupIds(new Set())

    const emptyTargets = new Set(
      normalizedActionGroups
        .flatMap(group => group.actions)
        .filter(action =>
          action.type === 'PUSH_INTEGRATION'
            ? (action.pushTargets ?? []).length === 0
            : !action.target.trim(),
        )
        .map(action => action.id),
    )
    if (emptyTargets.size > 0) {
      setEmptyTargetRowIds(emptyTargets)
      actionsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      return
    }
    setEmptyTargetRowIds(new Set())

    const { pairs: groupPairs } = editorToWorkflowGroups(
      normalizedActionGroups,
      roles,
      staff,
      availableIntegrations,
    )
    if (groupPairs.length === 0) {
      toast.error('Add at least one action before saving')
      setActionsError(true)
      actionsRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      setTimeout(() => setActionsError(false), 2500)
      return
    }

    const apiActionGroups = groupPairs.map(pair => pair.api)

    const workflowData = {
      title: title.trim(),
      selectionCriteria: selectionCriteria.trim(),
      propertyIds,
      propertyGroupIds,
      groupTypes,
      officeHours: officeHoursToApiValue(officeHours),
      customOfficeHoursSchedule:
        officeHours === OfficeHoursMode.CUSTOM_HOURS
          ? customSchedule
          : undefined,
      handoffInstructions: deriveInstructionsSummary(normalizedActionGroups),
      actionGroups: apiActionGroups,
    }

    setSaving(true)
    try {
      let saved: WorkflowWithActionGroups | null
      if (isEditing && id) {
        saved = isSuperAdmin
          ? await workflowsApiClient.updateAdminHandoffWorkflowForCompany(
              id,
              currentCompany.id,
              workflowData,
            )
          : await workflowsApiClient.updateHandoffWorkflow(id, workflowData)
      } else {
        saved = isSuperAdmin
          ? await workflowsApiClient.createAdminHandoffWorkflowForCompany(
              currentCompany.id,
              workflowData,
            )
          : await workflowsApiClient.createHandoffWorkflow(workflowData)
      }
      if (!saved) {
        toast.error('Failed to save handoff rule')
        return
      }

      // Sync directory mentions across each editor group's action targets
      // to action_contacts on the saved action groups. Best-effort — a sync
      // failure shouldn't roll back a successful workflow save.
      try {
        const savedGroups = saved.actionGroups ?? []
        if (savedGroups.length > 0) {
          const groupIds = savedGroups
            .map(group => group.id)
            .filter((groupId): groupId is string => !!groupId)
          const existing = (
            await Promise.all(
              groupIds.map(groupId =>
                directoryApiClient.listActionContacts(groupId),
              ),
            )
          ).flat()
          // groupPairs[index] aligns with savedGroups[index] for the first N
          // groups; trailing entries in savedGroups are push-only ALWAYS
          // groups and have no mentions to sync, so we stop at pairs.length.
          for (let index = 0; index < groupPairs.length; index++) {
            const savedId = savedGroups[index]?.id
            const pair = groupPairs[index]
            if (!savedId || !pair) continue
            // Parse per chip so each ActionContact row records its channel.
            const mentions = pair.editor.actions
              .filter(action => action.type !== 'PUSH_INTEGRATION')
              .map(action => {
                const parsed = parseMentions(action.target, roles, staff)
                return {
                  channel: contactChannelOf(
                    action.type as Exclude<ActionRowType, 'PUSH_INTEGRATION'>,
                  ),
                  roleIds: parsed.roleIds,
                  staffIds: parsed.staffIds,
                }
              })
            const groupExisting = existing.filter(
              c => c.actionGroupId === savedId,
            )
            await syncContactsForActionGroups({
              actionGroupId: savedId,
              mentions,
              existing: groupExisting,
            })
          }
        }
      } catch (syncErr) {
        console.error('Failed to sync directory mentions', syncErr)
        toast.error(
          'Saved the rule, but failed to link some directory mentions',
        )
      }

      toast.success(isEditing ? 'Rule updated' : 'Rule created')
      await refreshWorkflows()
      navigate('/actions')
    } catch (e: unknown) {
      console.error('Failed to save handoff rule', e)
      toast.error('Failed to save handoff rule')
    } finally {
      setSaving(false)
    }
  }

  if (isEditing && rulesLoading && !initialRule) {
    return <PageSkeleton />
  }

  if (isEditing && initialRule && !actionsHydrated) {
    return <PageSkeleton />
  }

  if (isEditing && !initialRule) {
    return (
      <div className="h-full overflow-y-auto pt-16">
        <div className="space-y-4 p-6 pt-0 max-w-3xl mt-6">
          <Heading variant="page-title">Rule not found</Heading>
          <Text variant="muted">
            The handoff rule you're looking for doesn't exist or was deleted.
          </Text>
          <Button onClick={() => navigate('/actions')}>Back to actions</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="space-y-6 p-6 pt-0 pb-24 max-w-3xl mt-6">
        <Heading variant="page-title">
          {isEditing ? 'Edit handoff rule' : 'New handoff rule'}
        </Heading>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Rule name</Label>
            <Input
              value={title}
              onChange={e => {
                setTitle(e.target.value)
                if (titleError && e.target.value.trim()) setTitleError(false)
              }}
              placeholder="e.g. Lock-out, Maintenance, James rule"
              maxLength={100}
              className={
                titleError
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              }
            />
            {titleError && (
              <p className="text-xs text-destructive">Rule name is required.</p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label>Properties</Label>
            <SearchDropdown<Property | null>
              items={propertyDropdownItems}
              selectedItems={selectedProperties}
              onSelect={handlePropertySelect}
              multiSelect
              placeholder="Select the properties where this handoff rule applies"
              searchPlaceholder="Search your properties..."
              buttonClassName="w-full"
              emptyMessage="No properties found"
              renderItem={(item, isSelected) => (
                <>
                  {item.icon}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{item.label}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground truncate">
                        {item.sublabel}
                      </span>
                      {item.data?.propertyType && (
                        <Badge variant="outline" className="text-xs h-4 px-1">
                          {String(item.data.propertyType)
                            .replace(/_/g, ' ')
                            .toLowerCase()
                            .replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {isSelected && <Check className="h-4 w-4 shrink-0" />}
                </>
              )}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Groups</Label>
            <SearchDropdown<GroupType | null>
              items={groupTypeDropdownItems}
              selectedItems={selectedGroupTypes}
              onSelect={handleGroupTypeSelect}
              multiSelect
              placeholder="Select the caller groups this rule applies to"
              searchPlaceholder="Search groups..."
              buttonClassName="w-full"
              emptyMessage="No groups found"
              renderItem={(item, isSelected) => (
                <>
                  {item.icon}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{item.label}</div>
                    <div className="text-xs text-muted-foreground truncate mt-1">
                      {item.sublabel}
                    </div>
                  </div>
                  {isSelected && <Check className="h-4 w-4 shrink-0" />}
                </>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>When</Label>
            <Select
              value={officeHours}
              onValueChange={v => setOfficeHours(v as OfficeHoursMode)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OFFICE_HOURS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {officeHours === OfficeHoursMode.CUSTOM_HOURS && (
            <div className="space-y-2">
              <Label>Custom schedule</Label>
              <WeeklyHoursSchedule
                value={customSchedule}
                onChange={setCustomSchedule}
              />
              <Text variant="muted" className="text-xs">
                These times are evaluated in the company timezone.
              </Text>
            </div>
          )}

          <div className="space-y-2">
            <Label>Scenario</Label>
            <Textarea
              value={selectionCriteria}
              onChange={e => {
                setSelectionCriteria(e.target.value)
                if (scenarioError && e.target.value.trim())
                  setScenarioError(false)
              }}
              placeholder="When the caller is asking about a lockout or building access issue."
              rows={2}
              className={`resize-none ${
                scenarioError
                  ? 'border-destructive focus-visible:ring-destructive'
                  : ''
              }`}
              maxLength={360}
            />
            {scenarioError ? (
              <p className="text-xs text-destructive">Scenario is required.</p>
            ) : (
              <Text variant="muted" className="text-xs">
                Describes when this rule should be selected upon the end of a
                session.
              </Text>
            )}
          </div>
        </div>

        <div
          ref={actionsRef}
          className={`border-t pt-6 space-y-2 ${
            actionsError
              ? 'rounded-md ring-2 ring-destructive ring-offset-2 transition-shadow'
              : ''
          }`}
        >
          <Heading variant="subsection-title">Actions</Heading>
          {emptyRoleMentionCount > 0 && (
            <div className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">
              <TriangleAlert className="h-4 w-4 shrink-0" />
              <span>
                {emptyRoleMentionCount === 1
                  ? '1 mention has no members, add a staff member to the role so actions can reach it.'
                  : `${emptyRoleMentionCount} mentions have no members, add staff members to those roles so actions can reach them.`}
              </span>
            </div>
          )}
          <ActionEditor
            value={actionGroups}
            onChange={newGroups => {
              setActionsError(false)
              setActionGroups(newGroups)

              // Drop ids from the error sets as the user fixes the inputs.
              setEmptyTargetRowIds(prev => {
                if (prev.size === 0) return prev
                const validIds = new Set(
                  newGroups
                    .flatMap(group => group.actions)
                    .filter(action =>
                      action.type === 'PUSH_INTEGRATION'
                        ? (action.pushTargets ?? []).length > 0
                        : !!action.target.trim(),
                    )
                    .map(action => action.id),
                )
                return new Set([...prev].filter(id => !validIds.has(id)))
              })

              setCriteriaErrorGroupIds(prev => {
                if (prev.size === 0) return prev
                if (newGroups.length <= 1) return new Set()
                const validIds = new Set(
                  newGroups
                    .filter(group => !!group.when.trim())
                    .map(group => group.id),
                )
                return new Set([...prev].filter(id => !validIds.has(id)))
              })
            }}
            pushOptions={pushOptions}
            pushFieldDropdownItems={dropdownItems}
            pushFieldFailedSources={failedSources}
            criteriaErrorGroupIds={criteriaErrorGroupIds}
            emptyTargetRowIds={emptyTargetRowIds}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => navigate('/actions')}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Create rule'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function PageSkeleton() {
  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="space-y-6 p-6 pt-0 max-w-3xl mt-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    </div>
  )
}
